import React, { Dispatch, FC, SetStateAction, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import Select from 'react-select'
import Input from 'uiKit/inputs/Input'
import { InputsContainer, SelectContainer, SelectLabel } from './InviteUserModal.styles'
import SelectorStyles from '../../../nlp/constants/selectorStyles'
import { ErrorsType, FormType } from './InviteUserModal'
import { getManagersLists } from '../../api/usersApi'
import { getUserRole, ROLE } from 'security'

const roleOptions = [
  {
    label: 'Carer',
    value: 'CARER',
  },
  {
    label: 'Manager',
    value: 'SUPERVISOR',
  },
]

interface Props {
  form: FormType
  setForm: Dispatch<SetStateAction<FormType>>
  errors: ErrorsType
  setErrors: Dispatch<SetStateAction<ErrorsType>>
  botId?: string
}

export const OneUserTab: FC<Props> = ({ form, setForm, errors, setErrors, botId }) => {
  const adminUser = useSelector((state: any) => state.adminUser)
  const [selectValue, setSelectValue] = useState({
    label: roleOptions[0].label,
    value: roleOptions[0].value,
  })

  const [managerOptions, setManagerOptions] = useState([])
  const [selectedManager, setSelectedManager] = useState(null)
  const [isRoleMenuOpen, setIsRoleMenuOpen] = useState(false)
  const [isManagerMenuOpen, setIsManagerMenuOpen] = useState(false)

  useEffect(() => {
    if (botId) {
      getManagersLists(botId).then(managers => {
        if (managers && Array.isArray(managers?.data)) {
          const formattedOptions = managers.data.map(manager => ({
            label: `${manager.first_name} ${manager.last_name}`,
            value: manager.email,
          }))
          setManagerOptions(formattedOptions)
          // Prefill Assign Manager only for Care Manager on this bot — Admin /
          // Careco Admin leave the field empty (prototype behaviour).
          const isCareManager = getUserRole(botId) === ROLE.MANAGER
          if (isCareManager && formattedOptions.length > 0) {
            const adminEmail = String(adminUser?.email || '').toLowerCase()
            const selfMatch =
              adminEmail &&
              formattedOptions.find(
                option => String(option.value || '').toLowerCase() === adminEmail,
              )
            const prefill = selfMatch || formattedOptions[0]
            setSelectedManager(prefill)
            setForm(prev => ({ ...prev, manager_email: prefill.value }))
            setErrors(prev => ({ ...prev, manager_email: '' }))
          } else {
            setSelectedManager(null)
            setForm(prev => ({ ...prev, manager_email: null }))
          }
        } else {
          setManagerOptions([])
          setSelectedManager(null)
          setForm(prev => ({ ...prev, manager_email: null }))
        }
      })
    }
  }, [botId, adminUser?.email, setForm, setErrors])

  // Close any open select menu when the modal's scrollable body scrolls
  useEffect(() => {
    const scrollable = document.getElementById('scrollable')
    if (!scrollable) return
    const onScroll = () => {
      if (isRoleMenuOpen) setIsRoleMenuOpen(false)
      if (isManagerMenuOpen) setIsManagerMenuOpen(false)
    }
    scrollable.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      scrollable.removeEventListener('scroll', onScroll)
    }
  }, [isRoleMenuOpen, isManagerMenuOpen])

  const onChangeHandler = (type, value) => {
    // Handle regular form fields and role selection
    if (type !== 'manager') {
      setForm(prevState => ({
        ...prevState,
        [type]: type === 'role' ? value.value : value,
      }))
    }

    // Clear errors when field is updated
    if (type !== 'role') {
      setErrors(prevState => ({
        ...prevState,
        [type]: '',
      }))
    }

    // Handle specific field types
    if (type === 'role') {
      setSelectValue(value)
    } else if (type === 'manager') {
      setSelectedManager(value)
      setForm(prevState => ({
        ...prevState,
        manager_email: value ? value.value : null,
      }))
      // Clear manager_email error when a manager is selected
      setErrors(prevState => ({
        ...prevState,
        manager_email: '',
      }))
    }
  }

  return (
    <>
      <Input
        title="Email"
        placeholder="Enter user’s email"
        value={form.email}
        onChange={e => onChangeHandler('email', e.target.value)}
        error={!!errors.email}
        errorMessage={errors.email}
      />
      <InputsContainer>
        <Input
          title="First Name"
          placeholder="Enter user’s first name"
          value={form.first_name}
          onChange={e => onChangeHandler('first_name', e.target.value)}
          error={!!errors.first_name}
          errorMessage={errors.first_name}
        />
        <Input
          title="Last Name"
          placeholder="Enter user’s last name"
          value={form.last_name}
          onChange={e => onChangeHandler('last_name', e.target.value)}
          error={!!errors.last_name}
          errorMessage={errors.last_name}
        />
      </InputsContainer>
      <SelectContainer>
        <SelectLabel>Role</SelectLabel>
        <Select
          isSearchable={false}
          options={roleOptions}
          value={selectValue}
          onChange={e => onChangeHandler('role', e)}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          closeMenuOnScroll={true}
          menuShouldScrollIntoView={false}
          menuIsOpen={isRoleMenuOpen}
          onMenuOpen={() => setIsRoleMenuOpen(true)}
          onMenuClose={() => setIsRoleMenuOpen(false)}
          styles={{
            ...SelectorStyles,
            menuPortal: base => ({ ...base, zIndex: 9999 }), // Ensure dropdown is above modal
            menu: base => ({ ...base, zIndex: 9999 }),
            indicatorSeparator: provided => ({
              ...provided,
              display: 'none',
            }),
            control: provided => ({
              ...provided,
              border: '1px solid var(--color-ghost-grey)',
              borderRadius: 10,
              outline: 'none',
              boxShadow: 'none',
              height: 45,
              cursor: 'pointer',
              '&:hover': {
                border: '1px solid var(--color-ghost-grey)',
                boxShadow: 'none',
              },
            }),
          }}
        />
      </SelectContainer>
      <SelectContainer>
        <SelectLabel>Assign Manager</SelectLabel>
        <Select
          isSearchable={true}
          options={managerOptions}
          value={selectedManager}
          onChange={e => onChangeHandler('manager', e)}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          closeMenuOnScroll={true}
          menuShouldScrollIntoView={false}
          menuIsOpen={isManagerMenuOpen}
          onMenuOpen={() => setIsManagerMenuOpen(true)}
          onMenuClose={() => setIsManagerMenuOpen(false)}
          styles={{
            ...SelectorStyles,
            menuPortal: base => ({ ...base, zIndex: 9999 }), // Ensure dropdown is above modal
            menu: base => ({ ...base, zIndex: 9999 }),
            indicatorSeparator: provided => ({
              ...provided,
              display: 'none',
            }),
            control: provided => ({
              ...provided,
              border: '1px solid var(--color-ghost-grey)',
              borderRadius: 10,
              outline: 'none',
              boxShadow: 'none',
              height: 45,
              cursor: 'pointer',
              '&:hover': {
                border: '1px solid var(--color-ghost-grey)',
                boxShadow: 'none',
              },
            }),
          }}
        />
        {errors.manager_email && (
          <p style={{ color: 'var(--color-text-error)', fontSize: '12px' }}>{errors.manager_email}</p>
        )}
      </SelectContainer>
    </>
  )
}
